import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Legend, ReferenceLine, ComposedChart, Area,
} from "recharts";

const API = "/trade_app/api/ml";

const COLORS = {
  bg:         "#0b1220",
  card:       "#111c30",
  cardEdge:   "#1f2d4a",
  axis:       "#3b4a6b",
  text:       "#e6eaf3",
  muted:      "#7a87a3",
  brand:      "#4cc9f0",
  spot:       "#3b82f6",
  sellPe:     "#22c55e",   // GREEN = sell PE (UP prediction)
  sellCe:     "#ef4444",   // RED   = sell CE (DOWN prediction)
  sideways:   "#64748b",
  sellPeSoft: "#16a34a",
  sellCeSoft: "#dc2626",
  ema10:      "#fde047",
  ema20:      "#fb923c",
  ema30:      "#c084fc",
  ema60:      "#38bdf8",
  opvDist:    "#a78bfa",
  vdDist:     "#60a5fa",
  futureRef:  "#94a3b8",
};

const Card = ({ title, value, sub, color = COLORS.brand, big }) => (
  <div style={{
    background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 10,
    padding: "14px 16px", minWidth: 160, flex: big ? 2 : 1
  }}>
    <div style={{ color: COLORS.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
    <div style={{ color, fontSize: big ? 28 : 20, fontWeight: 700, marginTop: 4 }}>{value}</div>
    {sub && <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 2 }}>{sub}</div>}
  </div>
);

const PanelHeader = ({ title, hint }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
    <div style={{ color: COLORS.brand, fontWeight: 700, fontSize: 14 }}>{title}</div>
    <div style={{ color: COLORS.muted, fontSize: 11 }}>{hint}</div>
  </div>
);

const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

// Derive the effective signal from CALIBRATED probability using a confidence
// band. The stored `predLabel` is based on raw probUp≥0.5 which, on 2026 data,
// is heavily DOWN-biased on out-of-distribution flat bars (82.7% of every-bar
// predictions come out DOWN). This helper instead gates on probUpCalibrated
// with a symmetric band: UP only if >=upTh, DOWN only if <=1-upTh, otherwise
// no signal (BLUE). Falls back to raw probUp if calibrated is missing.
const effectiveSignal = (b, upTh) => {
  if (!b) return null;
  const p = (b.probUpCalibrated != null) ? b.probUpCalibrated : b.probUp;
  if (p == null) return null;
  if (p >= upTh) return "UP";
  if (p <= 1 - upTh) return "DOWN";
  return null;
};

const ToolTip = ({ active, payload, label, threshold }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload || {};
  const hasPred = d.predLabel != null && d.indexClose != null;
  // Use calibrated-prob + confidence-band signal (not the DOWN-biased raw predLabel)
  const sig = effectiveSignal(d, threshold || 0.55);
  const sellPe = sig === "UP";
  const sellCe = sig === "DOWN";
  const probUp = d.probUp;
  const probUpCal = d.probUpCalibrated;
  const agreeUp = d.agreementUp;
  const agreeDn = d.agreementDown;
  return (
    <div style={{
      background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 8,
      padding: "10px 12px", color: COLORS.text, fontSize: 11, minWidth: 250,
      boxShadow: "0 6px 20px rgba(0,0,0,0.5)"
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: COLORS.brand }}>⏱ {d.barTime || label}</div>
      {d.indexClose != null && <div>📈 Spot: <b>{fmt(d.indexClose)}</b></div>}
      {d.futureClose != null && <div style={{ color: COLORS.muted }}>↪ +10 min actual: <b>{fmt(d.futureClose)}</b> ({fmt(d.actualReturnPct, 2)}%)</div>}
      {hasPred && (
        <div style={{ marginTop: 6, padding: "4px 8px", borderRadius: 4,
                       background: sellPe ? "#06371d" : (sellCe ? "#3b0a0a" : "#1e293b") }}>
          🤖 Signal: <b style={{ color: sellPe ? COLORS.sellPe : (sellCe ? COLORS.sellCe : COLORS.sideways) }}>
            {sellPe ? "🟢 SELL PE" : sellCe ? "🔴 SELL CE" : "⚪ HOLD / SIDEWAYS"}
          </b>
          {d.convictionLevel && d.convictionLevel !== "NONE" && (
            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                           color: d.convictionLevel === "STRONG" ? COLORS.sellPe : COLORS.futureRef }}>
              [{d.convictionLevel}]
            </span>
          )}
        </div>
      )}
      {probUp != null && (
        <div style={{ marginTop: 4 }}>
          P(UP) raw: <b>{fmtPct(probUp)}</b>
          {probUpCal != null && (
            <span> &nbsp; <span style={{ color: COLORS.muted }}>calib:</span> <b>{fmtPct(probUpCal)}</b></span>
          )}
          {d.probUpM3 != null && <span style={{ color: COLORS.muted }}> &nbsp;M3:{fmtPct(d.probUpM3)}</span>}
          {d.probUpM5 != null && <span style={{ color: COLORS.muted }}> &nbsp;M5:{fmtPct(d.probUpM5)}</span>}
          {d.probUpM10 != null && <span style={{ color: COLORS.muted }}> &nbsp;M10:{fmtPct(d.probUpM10)}</span>}
        </div>
      )}
      {(agreeUp != null || agreeDn != null) && (
        <div style={{ color: COLORS.muted, fontSize: 10 }}>
          Horizon agreement: ↑{agreeUp ?? 0}/3 · ↓{agreeDn ?? 0}/3
        </div>
      )}
      {d.actualLabel && (
        <div style={{ marginTop: 4 }}>
          ✅ Actual: <b style={{ color: d.actualLabel === "UP" ? COLORS.sellPe :
                                       d.actualLabel === "DOWN" ? COLORS.sellCe : COLORS.sideways }}>
            {d.actualLabel}</b>
          {d.match != null && <span style={{ marginLeft: 6,
            color: d.match ? COLORS.sellPe : COLORS.sellCe }}>
            {d.match ? "✓" : "✗"}</span>}
        </div>
      )}
      <hr style={{ borderColor: COLORS.cardEdge, margin: "6px 0" }} />
      {d.opvDist != null && <div>opvDist: <b>{fmt(d.opvDist)}</b> &nbsp; E10:{fmt(d.opvDE10)} &nbsp; E60:{fmt(d.opvDE60)}</div>}
      {d.vdDist != null  && <div>vdDist: <b>{fmt(d.vdDist)}</b> &nbsp; E10:{fmt(d.vdDE10)} &nbsp; E60:{fmt(d.vdDE60)}</div>}
    </div>
  );
};

const MlDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("NON_EXPIRY_NIFTY");
  const [dateGroups, setDateGroups] = useState({ train: [], validate: [], test: [], all: [] });
  const [selectedCategory, setSelectedCategory] = useState("test");
  const [selectedDate, setSelectedDate] = useState("");
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenStatus, setRegenStatus] = useState("idle");
  const [confThreshold, setConfThreshold] = useState(0.55);     // only show signals at or above this
  const [showOnlyStrong, setShowOnlyStrong] = useState(false);
  const [showMediumToo, setShowMediumToo] = useState(true);

  const regeneratePredictions = async () => {
    setRegenStatus("starting...");
    try {
      await axios.post(`${API}/regenerate`);
      setRegenStatus("running...");
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/regenerate/status`);
          if (!r.data.running) {
            clearInterval(poll);
            setRegenStatus(r.data.lastStatus);
            await refreshAll();
          }
        } catch (e) {}
      }, 2000);
    } catch (e) { setRegenStatus("error: " + e.message); }
  };

  const refreshAll = async () => {
    const [sum, dg, cols] = await Promise.all([
      axios.get(`${API}/accuracy/summary`),
      axios.get(`${API}/dates/grouped`),
      axios.get(`${API}/dataset/collections`),
    ]);
    setSummary(sum.data);
    setCollections(cols.data || []);
    const groups = dg.data || { train: [], validate: [], test: [], all: [] };
    setDateGroups(groups);
    const initDates = groups[selectedCategory] || groups["all"] || [];
    if (initDates.length && !selectedDate) setSelectedDate(initDates[initDates.length - 1]);
  };

  useEffect(() => { refreshAll(); }, []);
  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    axios.get(`${API}/day`, { params: { date: selectedDate, type: selectedDataset } })
      .then(r => setBars((r.data || []).sort((a, b) => (a.barIndex || 0) - (b.barIndex || 0))))
      .finally(() => setLoading(false));
  }, [selectedDate, selectedDataset]);

  // Helper: does a bar pass the user's confidence/conviction filter?
  // Now uses CALIBRATED-prob + confidence-band logic via effectiveSignal(),
  // so only bars with probUpCalibrated >= confThreshold (UP) or <= 1-confThreshold
  // (DOWN) pass. Bars inside the band return no signal and stay BLUE.
  const passesFilter = (b) => {
    const sig = effectiveSignal(b, confThreshold);
    if (!sig) return false;
    if (showOnlyStrong && b.convictionLevel !== "STRONG") return false;
    if (!showMediumToo && b.convictionLevel === "MEDIUM") return false;
    return true;
  };

  const dayStats = useMemo(() => {
    const predicted = bars.filter(b => b.predLabel != null);
    const actionable = predicted.filter(passesFilter);
    // Compute correctness against ACTUAL label using the effective signal,
    // not the stored (raw-based) predLabel.
    const matches = actionable.filter(b => {
      const sig = effectiveSignal(b, confThreshold);
      return sig && b.actualLabel && sig === b.actualLabel;
    }).length;
    const sellPe = actionable.filter(b => effectiveSignal(b, confThreshold) === "UP").length;
    const sellCe = actionable.filter(b => effectiveSignal(b, confThreshold) === "DOWN").length;
    const strong = actionable.filter(b => b.convictionLevel === "STRONG").length;
    return {
      nActionable: actionable.length,
      nPredicted: predicted.length,
      matches, hitRate: actionable.length ? matches / actionable.length : 0,
      sellPe, sellCe, strong,
    };
  }, [bars, confThreshold, showOnlyStrong, showMediumToo]);

  // Build full 09:15 → 15:30 timeline
  const fullTimeBars = useMemo(() => {
    if (!bars.length) return [];
    const barMap = {};
    bars.forEach(b => { if (b.barTime) barMap[b.barTime] = b; });
    const result = [];
    for (let h = 9; h <= 15; h++) {
      const mStart = h === 9 ? 15 : 0;
      const mEnd = h === 15 ? 31 : 60;
      for (let m = mStart; m < mEnd; m++) {
        const t = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        result.push(barMap[t] || { barTime: t });
      }
    }
    return result;
  }, [bars]);

  // Build a "persistent signal" stream: once a sell-PE (UP) or sell-CE (DOWN) signal
  // fires, that color stays active on the spot line until a different directional
  // signal overrides it. Bars where predLabel=SIDEWAYS or below-threshold keep the
  // last active color — matches trader intuition ("stay red until a new signal").
  // Overlap one bar at each transition to avoid a visible gap between segments.
  const chartData = useMemo(() => {
    // Pass 1 — compute persistent signal per bar, using the CALIBRATED-prob
    // effectiveSignal() helper (not the DOWN-biased stored predLabel).
    let active = null;   // "UP" | "DOWN" | null
    let firstSignalSeen = false;
    const signalStream = fullTimeBars.map(b => {
      if (passesFilter(b)) {
        const sig = effectiveSignal(b, confThreshold);
        if (sig === "UP" || sig === "DOWN") {
          active = sig;
          firstSignalSeen = true;
        }
      }
      return { bar: b, signal: firstSignalSeen ? active : null };
    });
    // Pass 2 — emit colored line fields with 1-bar overlap at transitions
    return signalStream.map((s, i) => {
      const b = s.bar;
      const cur = s.signal;
      const next = signalStream[i + 1]?.signal;
      // Include this bar in both adjacent series when transitioning (so lines connect)
      const transitioning = next && cur && next !== cur;
      return {
        ...b,
        spotSellPe: b.indexClose != null && (cur === "UP"   || (transitioning && next === "UP"))   ? b.indexClose : null,
        spotSellCe: b.indexClose != null && (cur === "DOWN" || (transitioning && next === "DOWN")) ? b.indexClose : null,
        spotNeutral: b.indexClose != null && cur === null ? b.indexClose : null,
        activeSignal: cur,
        // Preserve dot-style STRONG markers (smaller, subtle — to visually flag high-conviction bars)
        // Use effectiveSignal so markers match the colored line segments
        strongSellPeMarker: passesFilter(b) && effectiveSignal(b, confThreshold) === "UP"   && b.convictionLevel === "STRONG" ? b.indexClose : null,
        strongSellCeMarker: passesFilter(b) && effectiveSignal(b, confThreshold) === "DOWN" && b.convictionLevel === "STRONG" ? b.indexClose : null,
      };
    });
  }, [fullTimeBars, confThreshold, showOnlyStrong, showMediumToo]);

  const xInterval = Math.max(1, Math.floor(chartData.length / 22));
  const currentDs = collections.find(c => c.type === selectedDataset);

  return (
    <div style={{ padding: 20, background: COLORS.bg, minHeight: "100vh", color: COLORS.text,
                  fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ color: COLORS.brand, margin: 0, fontWeight: 700 }}>
          🧠 Nifty Non-Expiry — Option-Seller ML Dashboard
        </h2>
        <button onClick={regeneratePredictions}
                disabled={regenStatus === "running..."}
                style={{ background: regenStatus === "running..." ? COLORS.cardEdge : COLORS.brand,
                         color: "#062836", border: "none", padding: "8px 18px", borderRadius: 8,
                         cursor: regenStatus === "running..." ? "wait" : "pointer", fontWeight: 700, fontSize: 13 }}>
          {regenStatus === "running..." ? "⚙ Generating..." : "♺ Regenerate Predictions"}
        </button>
      </div>

      {/* Metric cards — tradeable-bar metrics (|ret|>=0.20%) are the meaningful ones for option selling */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <Card big title="Tradeable bars accuracy"
              value={summary?.tradeable ? `${(summary.tradeable.accuracy * 100).toFixed(1)}%` : "..."}
              sub={summary?.tradeable ? `${summary.tradeable.n?.toLocaleString()} bars with |ret|≥0.20%` : ""}
              color={summary?.tradeable?.accuracy >= 0.60 ? COLORS.sellPe :
                     (summary?.tradeable?.accuracy >= 0.55 ? COLORS.ema20 : COLORS.sellCe)} />
        <Card title="🟢 Sell-PE precision"
              value={summary?.tradeable ? `${(summary.tradeable.up_precision * 100).toFixed(1)}%` : "..."}
              sub={summary?.tradeable ? `${summary.tradeable.up_correct}/${summary.tradeable.up_predicted} hits` : ""}
              color={summary?.tradeable?.up_precision >= 0.75 ? COLORS.sellPe :
                     (summary?.tradeable?.up_precision >= 0.60 ? COLORS.ema20 : COLORS.sellCe)} />
        <Card title="🔴 Sell-CE precision"
              value={summary?.tradeable ? `${(summary.tradeable.down_precision * 100).toFixed(1)}%` : "..."}
              sub={summary?.tradeable ? `${summary.tradeable.down_correct}/${summary.tradeable.down_predicted} hits` : ""}
              color={summary?.tradeable?.down_precision >= 0.75 ? COLORS.sellPe :
                     (summary?.tradeable?.down_precision >= 0.60 ? COLORS.ema20 : COLORS.sellCe)} />
        <Card title="Today hit rate" value={`${(dayStats.hitRate * 100).toFixed(1)}%`}
              sub={`${dayStats.matches}/${dayStats.nActionable} actionable`}
              color={dayStats.hitRate >= 0.70 ? COLORS.sellPe :
                     (dayStats.hitRate >= 0.55 ? COLORS.ema20 : COLORS.sellCe)} />
        <Card title="Today signals"
              value={`${dayStats.sellPe}🟢 / ${dayStats.sellCe}🔴`}
              sub={`${dayStats.strong} strong (all 3 agree)`}
              color={COLORS.brand} />
        <Card title="Dataset"
              value={currentDs ? `${currentDs.days}d` : "..."}
              sub={currentDs ? `${currentDs.docs?.toLocaleString()} bars` : ""} />
      </div>

      {/* STRONG conviction precision strip */}
      {summary?.perConviction && (
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap",
                      background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`,
                      borderRadius: 8, padding: "10px 14px", fontSize: 12, color: COLORS.text }}>
          <b style={{ color: COLORS.brand }}>⭐ Precision by conviction</b>:
          {Object.entries(summary.perConviction).map(([k, v]) => (
            <span key={k} style={{ marginLeft: 8 }}>
              <b style={{ color: k === "STRONG" ? COLORS.sellPe : COLORS.ema20 }}>{k}</b>:&nbsp;
              <b>{(v.precision * 100).toFixed(1)}%</b>&nbsp;
              <span style={{ color: COLORS.muted }}>({v.correct}/{v.total})</span>
            </span>
          ))}
        </div>
      )}

      {/* Legend / How to read */}
      <div style={{
        background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 8,
        padding: "10px 14px", marginBottom: 12, fontSize: 12, color: COLORS.text, lineHeight: 1.55
      }}>
        <b style={{ color: COLORS.brand }}>📘 How to read</b>:
        &nbsp;<span style={{ color: COLORS.sellPe, fontWeight: 700 }}>● GREEN = SELL PE</span> (calibrated P(UP) ≥ confidence threshold → put will lose value)
        &nbsp;·&nbsp;<span style={{ color: COLORS.sellCe, fontWeight: 700 }}>● RED = SELL CE</span> (calibrated P(UP) ≤ 1 − threshold → call will lose value)
        &nbsp;·&nbsp;<span style={{ color: COLORS.sideways, fontWeight: 700 }}>● BLUE = no signal</span> (inside the confidence band — model is uncertain, stay out).
        The <b>spot line changes color</b> and <b>stays that color</b> until a new directional signal flips it.
        White-ringed dots mark <b>STRONG conviction</b> bars (all 3 horizons M=3/M=5/M=10 agree).
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 14, marginBottom: 14, alignItems: "center", flexWrap: "wrap",
                    background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 8, padding: "12px 16px" }}>
        <div>
          <label style={{ color: COLORS.muted, fontSize: 11, textTransform: "uppercase", marginRight: 8 }}>Dataset</label>
          <select value={selectedDataset} onChange={e => setSelectedDataset(e.target.value)}
                  style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.cardEdge}`, padding: "6px 10px", borderRadius: 6 }}>
            {collections.map(c => <option key={c.type} value={c.type}>{c.type.replace(/_/g, " ")} ({c.days} days)</option>)}
          </select>
        </div>
        <div>
          <label style={{ color: COLORS.muted, fontSize: 11, textTransform: "uppercase", marginRight: 8 }}>Split</label>
          <select value={selectedCategory} onChange={e => {
                    setSelectedCategory(e.target.value);
                    const catDates = dateGroups[e.target.value] || [];
                    if (catDates.length) setSelectedDate(catDates[catDates.length - 1]);
                  }}
                  style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.cardEdge}`, padding: "6px 10px", borderRadius: 6 }}>
            <option value="train">Train ({dateGroups.train?.length || 0})</option>
            <option value="validate">Validate ({dateGroups.validate?.length || 0})</option>
            <option value="test">Test ({dateGroups.test?.length || 0})</option>
            <option value="all">All ({dateGroups.all?.length || 0})</option>
          </select>
        </div>
        <div>
          <label style={{ color: COLORS.muted, fontSize: 11, textTransform: "uppercase", marginRight: 8 }}>Date</label>
          <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                  style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.cardEdge}`, padding: "6px 10px", borderRadius: 6 }}>
            {(dateGroups[selectedCategory] || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ marginLeft: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ color: COLORS.muted, fontSize: 11, textTransform: "uppercase" }}>
            Confidence ≥ {(confThreshold * 100).toFixed(0)}%
          </label>
          <input type="range" min="0.50" max="0.90" step="0.01"
                 value={confThreshold} onChange={e => setConfThreshold(Number(e.target.value))}
                 style={{ width: 160 }} />
        </div>
        <label style={{ fontSize: 11, color: COLORS.muted, display: "flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" checked={showOnlyStrong} onChange={e => setShowOnlyStrong(e.target.checked)} />
          STRONG only
        </label>
      </div>

      {loading ? <div style={{ color: COLORS.muted, padding: 20 }}>Loading…</div> : (
        <>
          {/* PANEL 1: Index with sell-PE / sell-CE signal overlay */}
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <PanelHeader title="📈 Spot Index — with option-selling signals"
                         hint="Blue = spot price · 🟢 sell PE (UP) · 🔴 sell CE (DOWN) · larger dot = STRONG conviction" />
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="spotGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.spot} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={COLORS.spot} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke={COLORS.axis} opacity={0.4} />
                <XAxis dataKey="barTime" interval={xInterval} tick={{ fill: COLORS.muted, fontSize: 10 }} stroke={COLORS.axis} />
                <YAxis domain={["auto", "auto"]} tick={{ fill: COLORS.muted, fontSize: 10 }} stroke={COLORS.axis} />
                <Tooltip content={(p) => <ToolTip {...p} threshold={confThreshold} />} cursor={{ stroke: COLORS.muted, strokeDasharray: "3 3" }} />
                <Legend wrapperStyle={{ color: COLORS.muted, fontSize: 11 }} />
                <Area type="monotone" dataKey="indexClose" fill="url(#spotGrad)" stroke="none" legendType="none" name="" />
                {/* Three colored line segments — blue (no signal), green (sell PE / UP), red (sell CE / DOWN).
                    The active color PERSISTS along the spot line until a new directional signal flips it. */}
                <Line type="monotone" dataKey="spotNeutral" stroke={COLORS.spot}  strokeWidth={2.5} dot={false}
                      name="🔵 No signal"  connectNulls={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="spotSellPe"  stroke={COLORS.sellPe} strokeWidth={3}   dot={false}
                      name="🟢 SELL PE"    connectNulls={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="spotSellCe"  stroke={COLORS.sellCe} strokeWidth={3}   dot={false}
                      name="🔴 SELL CE"    connectNulls={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* PANEL 2: opvDist + its EMAs */}
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <PanelHeader title="💰 opvDist — option-premium-volume distance & EMAs"
                         hint="opvDist above EMAs → CE-heavy flow (bullish squeeze) · below → PE-heavy flow" />
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 6" stroke={COLORS.axis} opacity={0.4} />
                <XAxis dataKey="barTime" interval={xInterval} tick={{ fill: COLORS.muted, fontSize: 10 }} stroke={COLORS.axis} />
                <YAxis tick={{ fill: COLORS.muted, fontSize: 10 }} stroke={COLORS.axis} />
                <Tooltip content={(p) => <ToolTip {...p} threshold={confThreshold} />} cursor={{ stroke: COLORS.muted, strokeDasharray: "3 3" }} />
                <Legend wrapperStyle={{ color: COLORS.muted, fontSize: 11 }} />
                <ReferenceLine y={0} stroke={COLORS.axis} />
                <Line type="monotone" dataKey="opvDist" stroke={COLORS.opvDist} strokeWidth={2} dot={false} name="opvDist" />
                <Line type="monotone" dataKey="opvDE10"  stroke={COLORS.ema10} strokeWidth={1.2} dot={false} name="EMA 10" />
                <Line type="monotone" dataKey="opvDE20"  stroke={COLORS.ema20} strokeWidth={1.2} dot={false} name="EMA 20" />
                <Line type="monotone" dataKey="opvDE30"  stroke={COLORS.ema30} strokeWidth={1.2} dot={false} name="EMA 30" />
                <Line type="monotone" dataKey="opvDE60"  stroke={COLORS.ema60} strokeWidth={1.5} dot={false} name="EMA 60" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* PANEL 3: vdDist + its EMAs */}
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardEdge}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
            <PanelHeader title="🎚 vdDist — vega×delta-diff distance & EMAs"
                         hint="vdDist above EMAs → flow favors UP · below → flow favors DOWN" />
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 6" stroke={COLORS.axis} opacity={0.4} />
                <XAxis dataKey="barTime" interval={xInterval} tick={{ fill: COLORS.muted, fontSize: 10 }} stroke={COLORS.axis} />
                <YAxis tick={{ fill: COLORS.muted, fontSize: 10 }} stroke={COLORS.axis} />
                <Tooltip content={(p) => <ToolTip {...p} threshold={confThreshold} />} cursor={{ stroke: COLORS.muted, strokeDasharray: "3 3" }} />
                <Legend wrapperStyle={{ color: COLORS.muted, fontSize: 11 }} />
                <ReferenceLine y={0} stroke={COLORS.axis} />
                <Line type="monotone" dataKey="vdDist"  stroke={COLORS.vdDist} strokeWidth={2} dot={false} name="vdDist" />
                <Line type="monotone" dataKey="vdDE10"  stroke={COLORS.ema10} strokeWidth={1.2} dot={false} name="EMA 10" />
                <Line type="monotone" dataKey="vdDE20"  stroke={COLORS.ema20} strokeWidth={1.2} dot={false} name="EMA 20" />
                <Line type="monotone" dataKey="vdDE30"  stroke={COLORS.ema30} strokeWidth={1.2} dot={false} name="EMA 30" />
                <Line type="monotone" dataKey="vdDE60"  stroke={COLORS.ema60} strokeWidth={1.5} dot={false} name="EMA 60" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default MlDashboard;
